/* eslint-disable max-lines */
import { useCallback, useEffect, useState } from 'react';
import { useHomepageTiles } from '../../../Experiments/HomepageTilesExperiment';
import { useCTA } from '../../../hooks/useCTA';
import { useRouter } from '../../../hooks/useRouter';
import { renderQuery, useSearch } from '../../../hooks/useSearch';
import { useStatefulScrollLock } from '../../../hooks/useStatefulScrollLock';
import { useWhiteLabelCustomisations } from '../../../hooks/useWhiteLabelCustomisations';
import { useMobile } from '../../../services/screenSize/ScreenSizeService';
import { DesktopNavMenu } from '../DesktopNavMenu/DesktopNavMenu';
import { HeaderContainer } from '../HeaderContainer/HeaderContainer';
import { MobileMenu } from '../MobileMenu/MobileMenu';
import { OverlayMask } from '../OverlayMask/OverlayMask';
import { SearchBar } from '../SearchBar/SearchBar';
import { CompactSearchIcon, CompactSearchIconBackground } from '../SearchBar/SearchBar.styles';
import { Props as SearchHeaderProps } from '../SearchHeader';
import {
  BackArrowIcon,
  CloseSearchButton,
  EmptyLogoContainer,
  HamburgerContainer,
  HomepageEmptyLogoContainer,
  StyledClickableLogo,
  StyledCTADot,
  StyledHamburgerIcon,
} from '../SearchHeader.styles';
import { SlideOut } from '../SlideOut/SlideOut';
import { Suggestions } from '../Suggestions/Suggestions';
import { SuggestionData } from '../Suggestions/types';
import { Transition } from '../Transition/Transition';
import { useSearchFilters } from '../filters/useSearchFilters';
import { useSuggestionHandler } from '../useSuggestionHandler';

export const SearchHeaderV1 = ({
  placeholder,
  disableSearch,
  compactSearch,
  searchSpecialty,
  showLoginDropdown,
  onClear,
  hideLogo,
  practitionerProfile,
}: SearchHeaderProps) => {
  const showExternalActions = useWhiteLabelCustomisations().showExternalActions;

  const [filters] = useSearchFilters();

  const {
    mobileSearchText,
    setMobileSearchText,
    focusMobileSearch,
    setFocusMobileSearch,
    desktopSearchText,
    setDesktopSearchText,
    focusDesktopSearch,
    setFocusDesktopSearch,
    expandSearch,
    setExpandSearch,
    toggleMobileSearch,
    desktopSearchInputRef,
  } = useSearch();

  const { setLocked: setSearchScrollLock } = useStatefulScrollLock();

  const [showingMobileMenu, setShowingMobileMenu] = useState<boolean>(false);
  const mobile = useMobile();
  const desktop = !mobile;
  const homepageTilesExperiment = useHomepageTiles();

  const searchText = mobile ? mobileSearchText : desktopSearchText;
  const setSearchText = mobile ? setMobileSearchText : setDesktopSearchText;
  const focusSearch = mobile ? focusMobileSearch : focusDesktopSearch;

  const setFocusSearch = useCallback(
    (val: boolean) => {
      mobile ? setFocusMobileSearch(val) : setFocusDesktopSearch(val);
    },
    [mobile, setFocusDesktopSearch, setFocusMobileSearch],
  );

  const setSearchFocused = useCallback(
    (val: boolean) => {
      setSearchScrollLock(val);
      setFocusSearch(val);
      setExpandSearch(val);
      onClear?.();
    },
    [setSearchScrollLock, setFocusSearch, setExpandSearch, onClear],
  );

  const openMenu = useCallback(() => {
    setSearchScrollLock(true);
    setShowingMobileMenu(true);
  }, [setSearchScrollLock, setShowingMobileMenu]);

  const closeMenu = useCallback(() => {
    setSearchScrollLock(false);
    setShowingMobileMenu(false);
  }, [setSearchScrollLock, setShowingMobileMenu]);

  const runSearchFromSuggestion = useSuggestionHandler();

  const handleSuggestion = useCallback(
    (suggestion: SuggestionData) => {
      setFocusSearch(false);
      setExpandSearch(false);
      setSearchScrollLock(false);
      runSearchFromSuggestion(suggestion);
    },
    [runSearchFromSuggestion, setSearchScrollLock, setFocusSearch, setExpandSearch],
  );

  const onClickSearch = useCallback(() => {
    handleSuggestion({
      type: 'TextSuggestion',
      filters: { text: searchText ?? '' },
      id: `text-${searchText}`,
    });

    setSearchScrollLock(false);
  }, [handleSuggestion, setSearchScrollLock, searchText]);

  const handleClear = useCallback(() => {
    setSearchText('');
    onClear?.();
    desktopSearchInputRef.current?.focus();
  }, [desktopSearchInputRef, onClear, setSearchText]);

  const setText = useCallback(
    (text: string) => {
      setSearchText(text);
      desktopSearchInputRef.current?.focus();
    },
    [setSearchText, desktopSearchInputRef],
  );

  /** Upon unfocusing without picking a suggestion, we revert the search bar value to the old query */
  useEffect(() => {
    if (!focusSearch) {
      setSearchText(renderQuery(filters) ?? '');
    }
  }, [focusSearch, filters, setSearchText]);

  // When a specialty is passed form a parent open the menu and set the value
  useEffect(() => {
    if (mobile && (!searchSpecialty || expandSearch || !compactSearch)) return;
    if (!searchSpecialty || (!homepageTilesExperiment && !mobile)) return;

    toggleMobileSearch(true);

    setSearchText(
      renderQuery({
        topic: {
          treatment: searchSpecialty?.title,
        },
      }),
    );
  }, [
    compactSearch,
    expandSearch,
    focusSearch,
    searchSpecialty,
    setSearchText,
    toggleMobileSearch,
    mobile,
    homepageTilesExperiment,
  ]);

  const willShowSearchBar =
    (!disableSearch && !compactSearch) || (compactSearch && expandSearch && showExternalActions);

  const { cta } = useCTA();

  const showAvatarCTA = cta?.showMctCTA || cta?.showHealthInterestsCTA || cta?.showNewOfferAddedCTA;

  const homepage = useRouter()?.pathname === '/';

  return (
    <>
      {desktop && (
        <Transition showing={focusDesktopSearch}>
          <OverlayMask onClickOverlay={() => setSearchFocused(false)} />
        </Transition>
      )}
      <HeaderContainer
        focused={focusDesktopSearch}
        topNavExperiment={hideLogo}
        shorterNavBar={hideLogo && homepage}
      >
        {desktop && !hideLogo && (
          <StyledClickableLogo supportWhiteLabel textLogo={compactSearch || disableSearch} />
        )}
        {desktop && hideLogo && !homepage && <EmptyLogoContainer />}
        {desktop && hideLogo && homepage && !homepageTilesExperiment && (
          <HomepageEmptyLogoContainer />
        )}

        {mobile && (
          <>
            {!focusMobileSearch && (
              <StyledClickableLogo supportWhiteLabel textLogo={compactSearch || disableSearch} />
            )}
            {focusMobileSearch && <BackArrowIcon onClick={() => setSearchFocused(false)} />}
          </>
        )}

        {willShowSearchBar && (
          <SearchBar
            placeholder={placeholder}
            focused={focusSearch}
            setFocused={setSearchFocused}
            value={focusSearch ? searchText : renderQuery(filters)}
            onChange={setSearchText}
            onClickSearch={onClickSearch}
            onClear={handleClear}
            inputRef={desktopSearchInputRef}
          />
        )}

        {!expandSearch && compactSearch && (
          <CompactSearchIconBackground
            onClick={() => toggleMobileSearch(!focusSearch)}
            data-link-id="Header Search Icon"
          >
            <CompactSearchIcon name="Search" />
          </CompactSearchIconBackground>
        )}

        {desktop && (
          <>
            {focusDesktopSearch && (
              <CloseSearchButton onClick={() => setSearchFocused(false)}>
                Close Search
              </CloseSearchButton>
            )}
            {!focusDesktopSearch && showExternalActions && (
              <DesktopNavMenu showLoginDropdown={showLoginDropdown} />
            )}
          </>
        )}

        {mobile && showExternalActions && !focusMobileSearch && (
          <div>
            {showAvatarCTA && (
              <HamburgerContainer>
                <StyledCTADot />
              </HamburgerContainer>
            )}
            <StyledHamburgerIcon onClick={openMenu} />
          </div>
        )}
      </HeaderContainer>

      {mobile && showExternalActions && (
        <SlideOut showing={showingMobileMenu} onClickOverlay={closeMenu}>
          <MobileMenu onClickLink={closeMenu} onClickLogout={closeMenu} />
        </SlideOut>
      )}

      {focusSearch && (
        <Suggestions
          setValue={setText}
          searchTerm={searchText ?? ''}
          onChoose={handleSuggestion}
          enabledFilterOffset={!compactSearch && !practitionerProfile}
        />
      )}
    </>
  );
};
